// `configureIntegrationsData` — the injected-config seam (C/D, ARCFE-1016;
// the `configureTemplatesData` / `configureCodingData` / `configureWorkspaceData`
// pattern).
//
// WHY A SEAM AND NOT THE `@heidi/api/client` REGISTRY. The registry resolves the
// SCRIBE client family — clients pointed at ml-scribe, sharing its interceptors,
// its base URL and its auth contract. `inboxHttp` is none of those: it is a
// dedicated axios instance for the standalone **inbox-service**, based at
// `getDomains()?.inboxServiceUrl` with its own 30s deadline, its own
// `X-Client-Id`/`X-Client-Version` headers and its own `Heidi-Workspace-Id`
// resolution. Registering a second service's client in the scribe registry would
// make "resolve a scribe client" able to return something that is not one, which
// is a worse defect than the wiring it saves.
//
// This is not a fresh judgement call — it is the ratified one. The templates
// seam injects `communityHttp` and names THIS client as its precedent:
// "Same reasoning as the EHR `inboxHttp` precedent — a domain-specific client is
// injected as its own instance" (`packages/templates/src/data/config.ts`). The
// child change's task 3.1 specifies the same shape.
//
// The builder cannot move either way: `src/features/document-inbox/api/inboxHttp.ts`
// reads six app-side modules (workspace atoms, `getDomains`, `isTauri`,
// `getClientVersion`, the sync auth-token helper and the shared scribe error
// handler). It stays app-side, and what crosses is a LOADER for it rather than
// the built instance — see `loadInboxHttp` below for why that distinction is a
// build-correctness requirement and not a style choice. The census pins the
// builder as a staying file for exactly this reason.
//
// FORWARD NOTE: the name is package-scoped, not door-scoped, deliberately — app
// bootstrap makes ONE call. When `./ehr`, `./letters` or `./connected-apps` land
// they extend THIS config rather than inventing a second seam.
//
// THE FORWARD NOTE IS NOW LOAD-BEARING RATHER THAN ASPIRATIONAL: `./connected-apps`
// landed and took it up. `onOnboardingSessionCleared` below is that door's port
// and its `/data` zone reads this module by relative path, so the seam is
// package-scoped in FACT and not only in name. Two consequences worth stating
// because they are the reason a second seam was not opened:
//
//   - the file still lives under `./document-inbox/data`, which is where it was
//     born. Moving it to a package-root module would be tidier to read and is
//     not free: `manifest-boundary.test.ts` pins the product-area root doors by
//     EQUALITY and asserts that every production file is reached by one of its
//     scans, and a `src/*.ts` sibling is reached by none of them. The location
//     is a fact about that fixture, not a claim that this config belongs to the
//     inbox;
//   - a door's port is OPTIONAL in the type and fail-loud at the CALL (see
//     `requireIntegrationsDataPort`). One config shared by four doors cannot
//     make every door's port mandatory for every consumer without turning each
//     new port into an edit of every sibling door's test setup — 21 call sites
//     across 16 suites at the time this was written, none of which exercises a
//     connected-app.

import type { QueryClient } from '@tanstack/react-query';

import type { SubscribeToProblemSetStream } from '../../problem-charting/contracts/problem-set-stream';
import type { InboxAssigneeCandidate } from '../contracts/index';

/**
 * Minimal structural view of the inbox-service client.
 *
 * Kept structural so this package's public signatures never name axios types
 * (declaration portability in the standalone build) — the templates
 * `CommunityHttpClient` rule. Every method here is one the carved api modules
 * actually call; nothing is declared speculatively.
 */
export interface InboxHttpClient {
  /**
   * `params` values are `string | undefined`, not `string`. The booking-slots
   * search passes `BookingSlotsParams`, whose five filters are all OPTIONAL, and
   * an interface with optional properties has no index signature — so the
   * narrower `Record<string, string>` rejected it at the call site while the
   * app-side axios instance had always accepted it. Widening here rather than
   * casting at the call site keeps the moved body byte-identical, which is what
   * makes it reviewable as a rename. Axios drops `undefined` values from the
   * query string, so the shape describes what the caller may hand over rather
   * than what ends up on the wire.
   */
  get<T>(
    url: string,
    config?: { params?: Record<string, string | undefined> }
  ): Promise<{ data: T }>;
  post<T>(
    url: string,
    body?: unknown,
    config?: { headers?: Record<string, string> }
  ): Promise<{ data: T }>;
  patch<T>(url: string, body?: unknown): Promise<{ data: T }>;
  put<T>(url: string, body?: unknown): Promise<{ data: T }>;
  delete<T = unknown>(url: string): Promise<{ data: T }>;
}

/**
 * Minimal structural view of the clinical-coding service client (the SCC
 * axios instance the coding package builds, with its own token minting and
 * 401 re-mint). Structural for the same declaration-portability reason as
 * `InboxHttpClient`; `get` is the one method the problem typeahead calls.
 */
export interface ClinicalCodingHttpClient {
  get<T>(
    url: string,
    config?: { signal?: AbortSignal; timeout?: number }
  ): Promise<{ data: T }>;
}

/**
 * Minimal structural view of the app's unauthenticated template-gallery
 * client (`globalHttp`). Structural for the same declaration-portability
 * reason as `InboxHttpClient`; `get` is the one method the provider-list api
 * fn calls.
 */
export interface TemplateGalleryHttpClient {
  /**
   * `timeout` is part of the port: the app's axios instance carries no default
   * deadline, so each api fn supplies its own or the query hangs pending on a
   * stalled request.
   */
  get<T>(
    url: string,
    config?: { params?: Record<string, string | undefined>; timeout?: number }
  ): Promise<{ data: T }>;
}

export interface IntegrationsDataConfig {
  /**
   * LOADER for the app-built inbox-service client — deliberately a function,
   * not the client itself.
   *
   * A different service from ml-scribe, which is why it is injected rather than
   * resolved from the scribe registry. The indirection on top of that is a
   * BUILD-CORRECTNESS requirement, learned the hard way (ARCFE-1016):
   *
   * the app-side builder's module cone is 1,187 modules — it reaches
   * `getDomains`, the sync auth-token helper and the shared scribe error
   * handler, which in turn reach the Rematch store. The store is an ASYNC
   * module under webpack/rspack (it awaits its logger and model barrel before
   * `init()`), and async-module status is contagious to every importer. Handing
   * the built client to `configureIntegrationsData` forced the wiring module —
   * and therefore `_app` — to pull that whole cone at module scope, which
   * reordered async-module settlement and made Next read the store export
   * before its `await` resolved: `ReferenceError: Cannot access 'v' before
   * initialization`, during "Collecting page data", on pages with no
   * connection to this domain.
   *
   * A loader restores the pre-carve timing: before the move, the builder was
   * only evaluated when a page actually used inbox data. Nothing local catches
   * this class — tsc, jest and lint are all blind to module-initialisation
   * order in a bundled chunk — so the shape is the guard.
   *
   * May return the client or a promise of it; the façade awaits either, so
   * tests can pass a plain `() => double` and the app can pass a dynamic
   * `import()`.
   */
  loadInboxHttp: () => InboxHttpClient | Promise<InboxHttpClient>;
  /**
   * Active workspace id.
   *
   * Crosses the seam rather than being imported: no package `/data` zone may
   * value-import `@heidi/workspace/data` (the pilot law, restated as this
   * change's task 3.3 — "`data/` files consume `useWorkspaceId` from the seam").
   * Measured true of every existing package at this commit.
   *
   * It is a HOOK, so it obeys the rules of hooks at the call site: the carved
   * queries call it unconditionally at the top of their own hook body, exactly
   * as they called the workspace door before the move.
   */
  useWorkspaceId: () => string | null | undefined;
  /**
   * Fired by `./connected-apps` where `clearIntegrationOnboardingSession()`
   * fired, after a session delete has invalidated the caches it evicts.
   *
   * A PORT, because the state it resets is app-side: seven per-field jotai
   * atoms in `src/atoms/integrationOnboardingSessionAtoms.ts`, which a package
   * `/data` zone may not reach. It is the simplest port shape there is — zero
   * arguments, no return, no failure mode — which is the measured answer to the
   * family record's claim that atoms are "not a port shape either": what the
   * write needs is not the atoms but the one effect that resets them.
   *
   * OPTIONAL IN THE TYPE, MANDATORY AT THE CALL. See
   * `requireIntegrationsDataPort` for why, and for what an unconfigured app
   * gets instead of a silent no-op.
   */
  onOnboardingSessionCleared?: () => void;
  /**
   * The app's provider-aware authentication verdict —
   * `useIsAuthenticated().isAuthenticated`, resolved app-side (ruling 65).
   *
   * A HOOK, not a getter: the verdict changes while the app runs and a captured
   * boolean would freeze it. Its two readers sit behind `./letters`, which reads
   * this module by relative path — the forward note above, taken up a second
   * time, so no second seam is opened for a second door.
   *
   * OPTIONAL IN THE TYPE, MANDATORY AT THE CALL, for the reason
   * `requireIntegrationsDataPort` records: one config shared by four doors
   * cannot make every door's port mandatory for every sibling's test setup.
   */
  useIsAuthenticated?: () => boolean;
  /**
   * The active team id (Mongo ObjectId string), resolved app-side.
   *
   * A HOOK / THUNK read at call time — capturing it at configure time is a
   * silent staleness bug.
   *
   * OPTIONAL IN THE TYPE, MANDATORY AT THE CALL, for the reason
   * `requireIntegrationsDataPort` records: one config shared by four doors
   * cannot make every door's port mandatory for every sibling's test setup.
   */
  useTeamId?: () => string | undefined;
  /**
   * Team-resolution context for inbox surfaces, resolved app-side.
   */
  useTeamContext?: () => {
    teamId: string | undefined;
    isResolving: boolean;
    hasNoTeam: boolean;
  };
  /**
   * The app's queryClient singleton, for the `./problem-charting` door's
   * module-scope invalidator (`invalidateSessionProblemSet`), which runs
   * outside React from the generate / regenerate paths and so cannot reach
   * `useQueryClient()`. The same instance the app hands `configureCodingData`,
   * and the same shape: the type is TanStack's own, imported as a type only.
   *
   * OPTIONAL IN THE TYPE, MANDATORY AT THE CALL, for the reason
   * `requireIntegrationsDataPort` records: one config shared by five doors
   * cannot make every door's port mandatory for every sibling's test setup.
   */
  queryClient?: QueryClient;
  /**
   * SSE transport for `./problem-charting`'s live pipeline feed
   * (`GET .../problem-set/stream`). A PORT because opening the stream needs
   * the app's domain resolution, its synchronous auth-token read and its SSE
   * header builder, none of which a package may import; the package receives
   * raw events and routes them into the query cache. The same arrangement the
   * session package uses for the legacy problems stream.
   *
   * OPTIONAL IN THE TYPE, MANDATORY AT THE CALL, for the reason
   * `requireIntegrationsDataPort` records.
   */
  subscribeToProblemSetStream?: SubscribeToProblemSetStream;
  /**
   * LOADER for the clinical-coding service client, used by
   * `./problem-charting`'s SNOMED typeahead. Sends the signed-in user's own
   * Heidi bearer to SCC's PUBLIC catalogue routes, since those
   * routes read no workspace or capability claim and authenticate an
   * ordinary IdP token rather than a minted coding-service token. The coding
   * package's client (`@heidi/coding/data`) is for SCC's case routes only,
   * which DO require that minted token; this loader is deliberately a
   * separate app-built client rather than a second use of that one. It
   * crosses the seam rather than being built here for the same reason as
   * `loadInboxHttp`: a `/data` zone of this package may not value-import
   * another domain package's door. A loader, not the client, for the
   * module-initialisation reason recorded on `loadInboxHttp`. May return the
   * client or a promise of it.
   *
   * OPTIONAL IN THE TYPE, MANDATORY AT THE CALL (`requireIntegrationsDataPort`).
   */
  loadClinicalCodingHttp?: () =>
    | ClinicalCodingHttpClient
    | Promise<ClinicalCodingHttpClient>;
  /**
   * LOADER for the app's unauthenticated template-gallery client
   * (`globalHttp`), used by `./ehr`'s country-scoped provider list. A
   * different service contract from ml-scribe, so it is injected rather than
   * resolved from the scribe registry; a LOADER rather than the client for
   * the module-initialisation reason recorded on `loadInboxHttp`.
   *
   * OPTIONAL IN THE TYPE, MANDATORY AT THE CALL (`requireIntegrationsDataPort`).
   */
  loadTemplateGalleryHttp?: () =>
    | TemplateGalleryHttpClient
    | Promise<TemplateGalleryHttpClient>;
  /**
   * Inbox assignee search. A port rather than an import, because this package
   * cannot value-import `@heidi/workspace/data`. The app maps members onto
   * `user_ref_id` candidates and drops members without one.
   *
   * Optional in the type, mandatory at the call (`requireIntegrationsDataPort`).
   */
  searchAssigneeCandidates?: (q: string) => Promise<InboxAssigneeCandidate[]>;
}

let config: IntegrationsDataConfig | null = null;

export const configureIntegrationsData = (next: IntegrationsDataConfig): void => {
  config = next;
};

/** Fail loudly on unconfigured use — never silently degrade. */
export const getIntegrationsDataConfig = (): IntegrationsDataConfig => {
  if (!config) {
    throw new Error(
      '[integrations/data] not configured — app bootstrap must call ' +
        'configureIntegrationsData() (see src/utils/integrations-data-wiring.ts); ' +
        'tests exercising real integrations data modules must configure a test ' +
        'double first.'
    );
  }
  return config;
};

/** Test-only reset so a suite cannot leak a double into the next one. */
export const resetIntegrationsDataConfigForTests = (): void => {
  config = null;
};

/**
 * One OPTIONAL port, or a throw naming the key that is missing.
 *
 * Optionality here is about the config being shared by four product-area doors,
 * not about the port being optional to the door that declares it. A required
 * field would be checked by tsc at exactly one production site — the app's
 * `integrations-data-wiring.ts` — and paid for at every test site of every
 * OTHER door, none of which can supply a meaningful double for a port they
 * never reach. So the type stays open and this accessor closes it, in the same
 * shape `inboxHttp` already uses for the loader: read at the CALL, so bootstrap
 * order cannot matter and an unwired app fails loudly at the moment the effect
 * was supposed to happen rather than degrading to a silent no-op.
 *
 * The two failure modes are deliberately distinguishable: no config at all
 * raises `getIntegrationsDataConfig`'s error naming the wiring module, and a
 * config missing this key raises the one below naming the key.
 */
export const requireIntegrationsDataPort = <K extends keyof IntegrationsDataConfig>(
  key: K
): NonNullable<IntegrationsDataConfig[K]> => {
  const port = getIntegrationsDataConfig()[key];
  if (port === undefined || port === null) {
    throw new Error(
      `[integrations/data] "${key}" is not configured — app bootstrap must pass ` +
        'it to configureIntegrationsData() (see src/utils/integrations-data-wiring.ts); ' +
        'a test exercising this path must configure a double for it.'
    );
  }
  return port;
};
